/**
 * 用户和角色管理API
 */
import apiClient from './client'

export interface User {
  id: number
  username: string
  email: string
  role: string
  roles?: Role[]
  avatar?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Role {
  id: number
  roleCode: string
  roleName: string
  roleType: string
  description?: string
}

export interface UserRole {
  id: number
  userId: number
  roleId: number
  contextId?: number
  contextType?: string
  assignedAt: string
  assignedBy?: number
}

export interface UserListResponse {
  users: User[]
  total: number
}

export interface RoleListResponse {
  roles: Role[]
  total: number
}

/**
 * 获取用户列表
 */
export async function getUsers(skip: number = 0, limit: number = 100): Promise<UserListResponse> {
  const response = await apiClient.get<any>('/v1/users', {
    params: { skip, limit }
  })
  return {
    users: response.users || [],
    total: response.total || 0
  }
}

/**
 * 获取用户详情
 */
export async function getUserById(userId: number): Promise<User> {
  const response = await apiClient.get<User>(`/v1/users/${userId}`)
  return response
}

/**
 * 获取所有角色列表
 */
export async function getRoles(): Promise<RoleListResponse> {
  const response = await apiClient.get<any>('/v1/roles')
  return {
    roles: response.roles || [],
    total: response.total || 0
  }
}

/**
 * 获取所有可用角色列表（用于角色管理）
 */
export async function getAvailableRoles(): Promise<Role[]> {
  const response: any = await apiClient.get('/v1/users/roles/available')
  if (response.code === 200 && Array.isArray(response.data)) {
    return response.data
  }
  return []
}

/**
 * 获取用户角色列表
 */
export async function getUserRoles(userId: number): Promise<Role[]> {
  try {
    const response: any = await apiClient.get(`/v1/auth/user/${userId}/roles`)
    // 后端返回的是List<RoleResponse>，需要转换为Role[]
    if (Array.isArray(response)) {
      return response.map((r: any) => ({
        id: r.roleId || r.id,
        roleCode: r.roleCode || r.roleKey,
        roleName: r.roleName,
        roleType: r.roleType || 'SYSTEM',
        description: r.description,
      }))
    }
    // 如果返回的是AjaxResult格式
    if (response.code === 200 && Array.isArray(response.data)) {
      return response.data.map((r: any) => ({
        id: r.roleId || r.id,
        roleCode: r.roleCode || r.roleKey,
        roleName: r.roleName,
        roleType: r.roleType || 'SYSTEM',
        description: r.description,
      }))
    }
    return []
  } catch (error: any) {
    console.error('获取用户角色失败:', error)
    throw error
  }
}

/**
 * 为用户分配角色
 */
export async function assignRoleToUser(
  userId: number,
  roleCode: string,
  roleId?: number,
  contextId?: number,
  contextType?: string
): Promise<{ message: string }> {
  const response: any = await apiClient.post(`/v1/users/${userId}/roles`, {
    roleCode,
    roleId,
    contextId,
    contextType
  })
  if (response.code === 200) {
    return {
      message: response.msg || '角色添加成功'
    }
  }
  throw new Error(response.msg || '角色添加失败')
}

/**
 * 移除用户角色
 */
export async function removeRoleFromUser(
  userId: number,
  roleId: number
): Promise<{ message: string }> {
  const response: any = await apiClient.delete(`/v1/users/${userId}/roles/${roleId}`)
  if (response.code === 200) {
    return {
      message: response.msg || '角色移除成功'
    }
  }
  throw new Error(response.msg || '角色移除失败')
}

/**
 * 更新用户信息
 */
export async function updateUser(
  userId: number,
  data: Partial<User>
): Promise<User> {
  const response = await apiClient.put(`/v1/users/${userId}`, data)
  return response
}

/**
 * 删除用户
 */
export async function deleteUser(userId: number): Promise<{ message: string }> {
  const response = await apiClient.delete(`/v1/users/${userId}`)
  return response
}

